import React, { useState } from "react";
import { Row, Col, message } from "antd";
import { useQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import useModal from "hooks/useModal";

// queries
import GET_MY_CHAPTERS from "graphql/queries/GetMyChapters";
import SKIP_CHAPTER from "graphql/mutations/SkipChapter";
import SKIP_TOPIC from "graphql/mutations/SkipTopic";
import UNSKIP_TOPIC from "graphql/mutations/UnskipTopic";

// components
import SkipTopicModal from "../class_details/SkipTopicModal";
import TopicList from "./TopicList";
import SkipChapterModal from "./SkipChapterModal";
import UnskipTopicModal from "./UnskipTopicModal";

// styles
import * as Styled from "./ChapterListStyles";

type Level = {
  id: string;
  title: string;
  chapters: Array<Chapter>;
};

type City = {
  name: string;
};

type Category = {
  name: string;
  skippedIfSpokenOnly: boolean;
};

type Topic = {
  id: number;
  title: string;
  cities: Array<City>;
  category: Category;
  studied: boolean;
  skipped: boolean;
  studiedCount: number;
  beingBookedOn: boolean;
};

type Chapter = {
  id: number;
  name: string;
  percentage: number;
  level: Level;
  topics: Array<Topic>;
  isDisabled: boolean; // visually only https://github.com/LTLOnline/flexi/pull/174
};

const ChapterList = (props: any) => {
  const { t } = useTranslation("my_level");

  const [targetChapterId, setTargetChapterId] = useState(0);
  const [targetTopicId, setTargetTopicId] = useState(0);
  const { params } = props;

  const [skipChapterModal, openSkipChapterModal, closeSkipChapterModal] =
    useModal();
  const [unskipTopicModal, openUnskipTopicModal, closeUnskipTopicModal] =
    useModal();
  const [skipTopicModal, openSkipTopicModal, closeSkipTopicModal] = useModal();

  const { data, loading, error } = useQuery(GET_MY_CHAPTERS, {
    variables: { levelId: params.levelId },
    fetchPolicy: "network-only",
  });

  const currentLevelId = params.levelId || data?.me.currentLevel.id;

  const handleOpenSkipChapterModal = (chapterId: number) => {
    openSkipChapterModal();
    setTargetChapterId(chapterId);
  };
  // -----
  // Unskip topic modal
  const handleOpenUnskipTopicModal = (topicId: number) => {
    openUnskipTopicModal();
    setTargetTopicId(topicId);
  };

  const handleOpenSkipTopicModal = (topicId: number) => {
    openSkipTopicModal();
    setTargetTopicId(topicId);
  };

  const [skipChapter] = useMutation(SKIP_CHAPTER, {
    refetchQueries: [
      {
        query: GET_MY_CHAPTERS,
        variables: {
          levelId: currentLevelId,
        },
      },
    ],
    onCompleted: () => {
      closeSkipChapterModal();
      message.success(t("Your chapter was skipped."));
    },
    onError: () => {
      closeSkipChapterModal();
      message.error(t("Cannot skip this chapter."));
    },
  });

  const [unskipTopic] = useMutation(UNSKIP_TOPIC, {
    refetchQueries: [
      {
        query: GET_MY_CHAPTERS,
        variables: {
          levelId: currentLevelId,
        },
      },
    ],
    onCompleted: () => {
      closeUnskipTopicModal();
      message.success(t("Your topic was un-skipped."));
    },
    onError: () => {
      closeUnskipTopicModal();
      message.error(t("Cannot un-skip this topic."));
    },
  });

  const [skipTopic] = useMutation(SKIP_TOPIC, {
    refetchQueries: [
      {
        query: GET_MY_CHAPTERS,
        variables: {
          levelId: currentLevelId,
        },
      },
    ],
    onCompleted: () => {
      closeSkipTopicModal();
      message.success(t("Your topic was skipped!"));
    },
    onError: () => {
      closeSkipTopicModal();
      message.error(t("Cannot skip this topic."));
    },
  });

  const handleClickConfirmSkipChapter = (chapterId: number) => {
    skipChapter({
      variables: {
        id: chapterId,
      },
    });
  };

  const handleConfirmUnskipTopic = () => {
    unskipTopic({
      variables: {
        id: targetTopicId,
      },
    });
  };

  const handleConfirmSkipTopic = () => {
    skipTopic({
      variables: {
        id: targetTopicId,
      },
    });
  };

  // DATA MANIPULATION
  const disableChapter = (chapter: Chapter) => {
    if (!chapter) return null;

    return {
      ...chapter,
      isDisabled: true,
    };
  };

  const chapters = data?.myChapters.map((chapter: Chapter) =>
    chapter.level.id === currentLevelId ? chapter : disableChapter(chapter)
  );

  // DATA PROCESSING
  const chapterTopicsArray: Array<Topic> = data?.myChapters?.flatMap(
    (chapter: Chapter) => chapter?.topics
  );
  const bookedTopicsArray: Array<Topic> = chapterTopicsArray?.filter(
    (topic: Topic) => topic?.beingBookedOn
  );

  // RENDERERS
  const renderChapterCard = (chapter: Chapter) => (
    <Styled.CardContainer isDisabled={chapter?.isDisabled}>
      {/* CARD TITLE: Chapter title */}
      <Styled.CardTitle isDisabled={chapter?.isDisabled}>
        <Styled.CardTitleText isDisabled={chapter?.isDisabled}>
          <a
            style={{ color: "inherit" }}
            href={`/chapter-details/${chapter.id}`}
          >
            {chapter?.level?.title} {chapter?.name}
          </a>
        </Styled.CardTitleText>
        <Styled.CardTitleText isDisabled={chapter?.isDisabled}>
          {chapter?.percentage}%
        </Styled.CardTitleText>
      </Styled.CardTitle>

      {/* CARD BODY: List of topics within this chapter */}
      <Styled.CardBody>
        {/* {console.log("chapter topics", chapter.topics)} */}
        <TopicList
          // TOPICS
          topics={chapter?.topics}
          // bookedTopics={bookedClasses}
          bookedTopics={bookedTopicsArray}
          isDisabled={chapter?.isDisabled}
          onOpenSkipTopicModal={handleOpenSkipTopicModal}
          onOpenUnskipTopicModal={handleOpenUnskipTopicModal}
        />
      </Styled.CardBody>
      {/* CARD FOOTER: CTA button */}
      <Styled.SkipThisChapterButton
        isDisabled={chapter?.isDisabled}
        type="button"
        onClick={() => handleOpenSkipChapterModal(chapter.id)}
      >
        {t("SKIP THIS CHAPTER").toUpperCase()}
      </Styled.SkipThisChapterButton>
    </Styled.CardContainer>
  );

  if (loading) {
    return <p>{t("Loading...")}</p>;
  }
  if (error) {
    return <p>{t("Loading data error")}</p>;
  }

  return (
    <Styled.Container>
      <Row>
        {chapters.map((chapter: Chapter) => (
          <Col lg={12} md={24} key={chapter?.id}>
            {renderChapterCard(chapter)}
          </Col>
        ))}
      </Row>
      <SkipChapterModal
        visible={skipChapterModal}
        targetChapterId={targetChapterId}
        onCancel={closeSkipChapterModal}
        onConfirm={handleClickConfirmSkipChapter}
      />
      <UnskipTopicModal
        visible={unskipTopicModal}
        onConfirm={handleConfirmUnskipTopic}
        onCancel={closeUnskipTopicModal}
      />
      <SkipTopicModal
        visible={skipTopicModal}
        onConfirm={handleConfirmSkipTopic}
        onCancel={closeSkipTopicModal}
      />
    </Styled.Container>
  );
};

export default ChapterList;
